import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Filter, SlidersHorizontal, Shield, UserCheck, CheckCircle, Loader2, Sparkles } from 'lucide-react';
import { Listbox } from '@headlessui/react';
import { categories } from '../data/agentsData';
import { AIAgent } from '../types';
import AgentProfile from './AgentProfile';
import { useTranslation } from 'react-i18next';
import { replicaService } from '../lib/services';

interface MarketplaceProps {
  onOpenAuthModal?: (mode: 'login' | 'signup') => void;
}

const Marketplace: React.FC<MarketplaceProps> = ({ onOpenAuthModal }) => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [priceRange, setPriceRange] = useState(1000);
  const [selectedAgent, setSelectedAgent] = useState<AIAgent | null>(null);
  const [allAgents, setAllAgents] = useState<AIAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation('marketing');

  useEffect(() => {
    replicaService.getMarketplace({ limit: '50' })
      .then(res => {
        const mapped: AIAgent[] = res.replicas.map(r => ({
          id: r.id,
          name: r.name,
          category: r.category,
          price: { amount: r.priceAmount, period: r.pricePeriod as 'hour' | 'month' },
          hasHumanGuardian: r.guardianAssignment !== 'none',
          image: r.avatarImageUrl || '',
          rating: r.rating,
          reviews: r.reviewCount,
          description: r.description,
          skills: r.personalityTraits ?? [],
          supportedTasks: [],
        }));
        setAllAgents(mapped);
      })
      .catch(() => setAllAgents([]))
      .finally(() => setLoading(false));
  }, []);

  const filteredAgents = allAgents.filter(agent => {
    const matchesCategory = selectedCategory === 'All' || agent.category === selectedCategory;
    const matchesSearch = agent.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPrice = agent.price.amount <= priceRange;
    return matchesCategory && matchesSearch && matchesPrice;
  });

  // Helper to generate role-based display name (removing -AI suffix)
  const getReplicaDisplayName = (agent: AIAgent): string => {
    // Convert "MARK-AI" to "Operations Replica" based on category
    const roleMap: { [key: string]: string } = {
      'HR': t('replica_directory.roles.hr'),
      'Marketing': t('replica_directory.roles.marketing'),
      'Finance': t('replica_directory.roles.finance'),
      'Customer Service': t('replica_directory.roles.customer_service'),
    };
    return roleMap[agent.category] || `${agent.category} Replica`;
  };

  if (selectedAgent) {
    return (
      <AgentProfile
        agent={selectedAgent}
        onBack={() => setSelectedAgent(null)}
        onOpenAuthModal={onOpenAuthModal}
      />
    );
  }

  // Hide the whole section when marketplace is empty and no filters are active
  if (!loading && allAgents.length === 0 && !searchQuery && selectedCategory === 'All') {
    return null;
  }

  return (
    <section className="section bg-hig-bg-primary text-hig-text-primary" id="replica-directory">
      <div className="container-hig">
        {/* Section header — Renamed from "AI Agent Marketplace" */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-6">
          <div className="max-w-xl">
            <h2 className="mb-3 text-3xl md:text-4xl font-semibold tracking-tight">
              {t('replica_directory.title')}
            </h2>
            <p className="body-large text-hig-text-secondary">
              {t('replica_directory.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="badge">
              {allAgents.length} {t('replica_directory.replicas_available')}
            </span>
          </div>
        </div>

        {/* Governance Banner */}
        <div className="doctrine-block mb-8">
          <p className="text-sm text-hig-text-primary">
            <Shield className="inline w-4 h-4 mr-2 text-hig-accent" />
            {t('replica_directory.governance_banner')}
          </p>
        </div>

        {/* Filters Panel */}
        <div className="glass p-5 mb-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            {/* Search */}
            <div className="md:col-span-5 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-hig-text-tertiary" size={16} />
              <input
                type="text"
                placeholder={t('replica_directory.search_placeholder')}
                className="w-full bg-hig-bg-tertiary border border-hig-border rounded-lg pl-9 pr-4 py-2 text-sm text-hig-text-primary placeholder-hig-text-tertiary focus:outline-none focus:ring-1 focus:ring-hig-accent focus:border-hig-accent transition-all"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Category Filter */}
            <div className="md:col-span-4">
              <Listbox value={selectedCategory} onChange={setSelectedCategory}>
                <div className="relative">
                  <Listbox.Button className="w-full bg-hig-bg-tertiary border border-hig-border rounded-lg px-4 py-2 text-sm text-left flex items-center justify-between text-hig-text-primary focus:outline-none focus:ring-1 focus:ring-hig-accent">
                    <span>{selectedCategory}</span>
                    <Filter size={14} className="text-hig-text-tertiary" />
                  </Listbox.Button>
                  <Listbox.Options className="absolute z-20 w-full mt-1 bg-hig-bg-elevated border border-hig-border rounded-lg shadow-xl overflow-hidden py-1 max-h-60 overflow-y-auto backdrop-blur-xl">
                    {categories.map((category) => (
                      <Listbox.Option
                        key={category}
                        value={category}
                        className={({ active }) =>
                          `cursor-pointer px-4 py-2 text-sm transition-colors ${active ? 'bg-hig-accent text-white' : 'text-hig-text-primary hover:bg-hig-bg-tertiary'
                          }`
                        }
                      >
                        {category}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </div>
              </Listbox>
            </div>

            {/* Price Range */}
            <div className="md:col-span-3 flex items-center gap-4">
              <SlidersHorizontal size={16} className="text-hig-text-tertiary" />
              <div className="flex-1 flex flex-col gap-1">
                <div className="flex justify-between text-xs text-hig-text-secondary">
                  <span>{t('replica_directory.max_price')}</span>
                  <span>${priceRange}/{t('replica_directory.month')}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="1000"
                  step="50"
                  value={priceRange}
                  onChange={(e) => setPriceRange(Number(e.target.value))}
                  className="w-full h-1 bg-hig-bg-tertiary rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-hig-accent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Replica Cards Grid — Redesigned to lead with guardian */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          layout
        >
          <AnimatePresence mode="popLayout">
          {filteredAgents.map((agent, index) => (
            <motion.div
              key={agent.id}
              className="card-interactive overflow-hidden group cursor-pointer flex flex-col h-full bg-hig-bg-elevated border-0"
              onClick={() => setSelectedAgent(agent)}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.35, delay: index * 0.05 }}
              whileHover={{ y: -8, transition: { duration: 0.25 } }}
              layout
            >
              {/* Guardian Section — Leads the card */}
              <div className="p-5 pb-4 border-b border-hig-divider">
                {agent.guardian ? (
                  <div className="flex items-center gap-3">
                    <img
                      src={agent.guardian.image}
                      alt={agent.guardian.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-hig-accent/30"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-hig-text-primary truncate">
                        {agent.guardian.name}
                      </p>
                      <p className="text-xs text-hig-text-tertiary truncate">
                        {agent.guardian.experience}+ {t('replica_directory.years_experience')}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <UserCheck className="w-4 h-4 text-hig-accent" />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-hig-bg-tertiary flex items-center justify-center">
                      <UserCheck className="w-5 h-5 text-hig-text-tertiary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-hig-text-secondary">
                        {t('replica_directory.community_trained')}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Replica Info */}
              <div className="p-5 flex-1 flex flex-col">
                {/* Category Badge */}
                <span className="inline-flex self-start items-center px-2 py-1 bg-hig-bg-tertiary text-hig-text-secondary text-xs font-medium rounded mb-3">
                  {agent.category}
                </span>

                {/* Role-based title (not -AI naming) */}
                <h3 className="font-semibold text-hig-text-primary group-hover:text-hig-accent transition-colors mb-2">
                  {getReplicaDisplayName(agent)}
                </h3>

                {/* Description emphasizing governance */}
                <p className="text-sm text-hig-text-secondary mb-4 line-clamp-2 leading-relaxed">
                  {t('replica_directory.trained_by_expert', { category: agent.category.toLowerCase() })}
                </p>

                {/* Governance Badges */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-hig-success/10 text-hig-success text-xs rounded">
                    <CheckCircle className="w-3 h-3" />
                    {t('replica_directory.human_controlled')}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-hig-accent/10 text-hig-accent text-xs rounded">
                    <Shield className="w-3 h-3" />
                    {t('replica_directory.decision_guardian')}
                  </span>
                </div>

                {/* CTA — Renamed from "View Model" */}
                <div className="mt-auto pt-4 border-t border-hig-divider">
                  <span className="text-sm font-medium text-hig-accent group-hover:translate-x-0.5 transition-transform inline-flex items-center">
                    {t('replica_directory.see_how_works')} →
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
          </AnimatePresence>
        </motion.div>

        {/* Loading state */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <Loader2 className="w-8 h-8 text-hig-accent animate-spin" />
            <p className="text-sm text-hig-text-tertiary">Loading replicas...</p>
          </div>
        )}

        {/* Empty state — early access */}
        {!loading && filteredAgents.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center justify-center py-24 text-center max-w-lg mx-auto"
          >
            <div className="w-16 h-16 rounded-2xl bg-hig-accent/10 flex items-center justify-center mb-6">
              <Sparkles className="w-7 h-7 text-hig-accent" />
            </div>
            <h3 className="text-xl font-semibold text-hig-text-primary mb-3">
              {searchQuery || selectedCategory !== 'All'
                ? t('replica_directory.no_replicas_found')
                : 'The marketplace opens with you.'}
            </h3>
            <p className="text-hig-text-secondary text-sm leading-relaxed mb-8">
              {searchQuery || selectedCategory !== 'All'
                ? t('replica_directory.adjust_filters')
                : 'HumanReplica® is in early access. Be among the first to deploy your replica and make it available to others.'}
            </p>
            {searchQuery || selectedCategory !== 'All' ? (
              <button
                onClick={() => { setSelectedCategory('All'); setSearchQuery(''); setPriceRange(1000); }}
                className="text-hig-accent hover:underline text-sm font-medium"
              >
                {t('replica_directory.clear_filters')}
              </button>
            ) : (
              <button
                onClick={() => onOpenAuthModal?.('signup')}
                className="btn-primary text-sm px-6 py-2.5"
              >
                Create your Replica →
              </button>
            )}
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default Marketplace;